/**
 * Глубокое слияние объектов с расширенными возможностями.
 *
 * @param {object} target - Целевой объект (будет мутирован).
 * @param {object[]} sources - Массив объектов-источников.
 * @param {Options} options - Объект с опциями.
 * @returns {object} Модифицированный целевой объект.
 */
export function deepAssignWithOptions(target, sources, options) {
  // Устанавливаем значения по умолчанию
  const {
    mode = 'merge',
    arrayStrategy = 'replace',
    conflictStrategy = 'replace',
    preserveExisting = false,
    deleteMissing = false,
    deep = true,
    maxDepth = Infinity,
    circular = 'ignore',
    _reverse = false,
    specialTypes = {}
  } = options;

  // Если reverse=true, target становится последним, sources идут до него, объединение идёт с конца к началу
  let sourcesToMerge;
  let targetToMerge;
  if (_reverse) {
    // target — последний, sources — все остальные
    sourcesToMerge = [...sources, target];
    targetToMerge = sourcesToMerge.pop(); // последний элемент — новый target
    sourcesToMerge = sourcesToMerge.reverse(); // объединяем с конца к началу
  } else {
    sourcesToMerge = sources;
    targetToMerge = target;
  }

  // Стек для отслеживания посещённых объектов (защита от циклов)
  const visited = new WeakSet();

  /**
   * Рекурсивно обновляет значение по ключу.
   * @param {object} parent - Родительский объект, в котором находится ключ.
   * @param {string|symbol} key - Ключ в родителе.
   * @param {*} targetValue - Текущее значение в цели.
   * @param {*} sourceValue - Значение из источника.
   * @param {number} depth - Текущая глубина рекурсии.
   */
  function mergeKey(parent, key, targetValue, sourceValue, depth) {
    // Проверка глубины
    if (depth > maxDepth) {
      if (circular === 'error') throw new Error('Max depth exceeded');
      return;
    }

    // Если одно из значений undefined/null, используем стратегию конфликта
    if (targetValue === undefined || targetValue === null ||
        sourceValue === undefined || sourceValue === null) {
      handleConflict(parent, key, targetValue, sourceValue);
      return;
    }

    // Проверка на циклические ссылки (если оба — объекты)
    if (typeof targetValue === 'object' && targetValue !== null &&
        typeof sourceValue === 'object' && sourceValue !== null) {
      if (visited.has(targetValue) || visited.has(sourceValue)) {
        if (circular === 'error') throw new Error('Circular reference detected');
        if (circular === 'replace') {
          parent[key] = null; // замена на null
        }
        // ignore – ничего не делаем, оставляем старое значение
        return;
      }
      visited.add(targetValue);
      visited.add(sourceValue);
    }

    // Определяем типы и конструкторы
    const typeTarget = typeof targetValue;
    const typeSource = typeof sourceValue;

    // Если типы различаются или это примитивы (кроме случая, когда оба объекта)
    if (typeTarget !== typeSource || typeTarget !== 'object' || targetValue === null) {
      handleConflict(parent, key, targetValue, sourceValue);
      return;
    }

    // Теперь оба — объекты (не null)
    const ctorTarget = targetValue.constructor;
    const ctorSource = sourceValue.constructor;

    // Специальный случай для mergeUpdate: объект с числовыми ключами, применяемый к массиву
    if (mode === 'mergeUpdate' && ctorTarget === Array && ctorSource === Object) {
      // Обрабатываем объект источника как индексы массива
      for (const numKey of Reflect.ownKeys(sourceValue)) {
        if (isNumericKey(numKey)) {
          const arrayIndex = parseInt(numKey, 10);
          if (arrayIndex < targetValue.length) {
            const targetItem = targetValue[arrayIndex];
            const sourceItem = sourceValue[numKey];
            if (targetItem && typeof targetItem === 'object' && targetItem !== null &&
                sourceItem && typeof sourceItem === 'object' && sourceItem !== null) {
              mergeKey(targetValue, arrayIndex, targetItem, sourceItem, depth + 1);
            } else if (sourceItem !== undefined) {
              handleConflict(targetValue, arrayIndex, targetItem, sourceItem);
            }
          }
        }
      }
      return;
    }

    // Если конструкторы разные — конфликт
    if (ctorTarget !== ctorSource) {
      handleConflict(parent, key, targetValue, sourceValue);
      return;
    }

    // Обработка в зависимости от конструктора
    if (ctorTarget === Object) {
      mergeObject(targetValue, sourceValue, depth + 1);
    } else if (ctorTarget === Array) {
      mergeArray(targetValue, sourceValue, depth + 1);
    } else if (ctorTarget === Date) {
      mergeDate(targetValue, sourceValue, parent, key);
    } else if (ctorTarget === Map) {
      mergeMap(targetValue, sourceValue, depth + 1);
    } else if (ctorTarget === Set) {
      mergeSet(targetValue, sourceValue);
    } else {
      // Другие типы — используем specialTypes или заменяем
      const typeName = targetValue.constructor.name;
      const strategy = specialTypes[typeName] || 'replace';
      if (strategy === 'replace') {
        parent[key] = sourceValue; // полная замена
      } else {
        // Для неизвестных типов просто игнорируем (можно добавить другие стратегии)
      }
    }

    // Удаляем из visited после обработки (для возможности повторного использования)
    if (typeof targetValue === 'object' && targetValue !== null) {
      visited.delete(targetValue);
    }
    if (typeof sourceValue === 'object' && sourceValue !== null) {
      visited.delete(sourceValue);
    }
  }

  /**
   * Обрабатывает конфликт на уровне ключа.
   */
  function handleConflict(parent, key, targetValue, sourceValue) {
    const targetType = targetValue === null ? 'null' : typeof targetValue;
    const sourceType = sourceValue === null ? 'null' : typeof sourceValue;

    switch (conflictStrategy) {
      case 'replace':
        parent[key] = sourceValue;
        break;
      case 'skip':
        // оставляем targetValue без изменений
        break;
      case 'throw':
        throw new Error(`Type conflict at key ${String(key)}: ${targetType} vs ${sourceType}`);
      default:
        parent[key] = sourceValue;
    }
  }

  /**
   * Проверяет, является ли ключ числовой строкой (для использования как индекс массива).
   */
  function isNumericKey(key) {
    if (typeof key !== 'string') return false;
    const num = parseInt(key, 10);
    return num.toString() === key && num >= 0;
  }

  /**
   * Слияние обычных объектов.
   */
  function mergeObject(targetObj, sourceObj, depth) {
    if (!deep) {
      // Поверхностное копирование свойств источника в целевой объект
      Object.assign(targetObj, sourceObj);
      return;
    }

    // Сначала удаляем ключи, если требуется
    if (deleteMissing) {
      for (const key of Reflect.ownKeys(targetObj)) {
        if (!(key in sourceObj)) {
          delete targetObj[key];
        }
      }
    }

    // Обрабатываем каждый ключ источника
    for (const key of Reflect.ownKeys(sourceObj)) {
      // mode: 'update' применяется только на верхнем уровне (depth === 0)
      // На рекурсивных уровнях используем 'merge'
      if (depth === 0 && mode === 'update' && !(key in targetObj)) {
        continue;
      }

      let targetVal = targetObj[key];
      const sourceVal = sourceObj[key];

      // Обработка mergeUpdate: если ключ числовой и target[key] — это массив,
      // применяем обновление к элементу массива по индексу
      if (mode === 'mergeUpdate' && isNumericKey(key) && Array.isArray(targetVal)) {
        const arrayIndex = parseInt(key, 10);
        if (arrayIndex < targetVal.length) {
          const targetItem = targetVal[arrayIndex];
          // Если оба — объекты, сливаем рекурсивно
          if (targetItem && typeof targetItem === 'object' && targetItem !== null &&
              sourceVal && typeof sourceVal === 'object' && sourceVal !== null) {
            mergeKey(targetVal, arrayIndex, targetItem, sourceVal, depth);
          } else if (sourceVal !== undefined) {
            // Иначе просто обновляем значение
            handleConflict(targetVal, arrayIndex, targetItem, sourceVal);
          }
        }
        continue; // переходим к следующему ключу
      }

      // Если оба значения — объекты и deep разрешён, рекурсивно сливаем
      // preserveExisting не применяется к объектам (их нужно сливать)
      if (targetVal && typeof targetVal === 'object' &&
          sourceVal && typeof sourceVal === 'object') {
        // Даже если preserveExisting, мы сливаем объекты рекурсивно
        mergeKey(targetObj, key, targetVal, sourceVal, depth);
      } else {
        // Для примитивов и новых ключей применяем preserveExisting (только на верхнем уровне)
        if (depth === 0 && preserveExisting && key in targetObj) {
          continue;
        }
        // Иначе разрешаем конфликт/присваивание
        handleConflict(targetObj, key, targetVal, sourceVal);
      }
    }
  }

  /**
   * Слияние массивов.
   */
  function mergeArray(targetArr, sourceArr, depth) {
    if (!deep) {
      // Поверхностная замена
      targetArr.length = 0;
      targetArr.push(...sourceArr);
      return;
    }

    switch (arrayStrategy) {
      case 'replace':
        targetArr.length = 0;
        targetArr.push(...sourceArr);
        break;

      case 'merge':
        // Поэлементное слияние
        const maxLen = Math.max(targetArr.length, sourceArr.length);
        for (let i = 0; i < maxLen; i++) {
          if (i < targetArr.length && i < sourceArr.length) {
            // Оба существуют
            const targetItem = targetArr[i];
            const sourceItem = sourceArr[i];
            if (targetItem && typeof targetItem === 'object' &&
                sourceItem && typeof sourceItem === 'object') {
              // Рекурсивно сливаем, передавая массив как родитель и индекс как ключ
              mergeKey(targetArr, i, targetItem, sourceItem, depth);
            } else {
              // Примитивы — разрешаем конфликт
              handleConflict(targetArr, i, targetItem, sourceItem);
            }
          } else if (i < sourceArr.length) {
            // Добавляем новый элемент, если не запрещено
            if (mode !== 'update' && !preserveExisting) {
              targetArr[i] = sourceArr[i];
            }
          }
          // если i >= sourceArr.length, ничего не делаем (оставляем хвост target)
        }
        break;

      case 'concat':
        if (mode !== 'update') {
          targetArr.push(...sourceArr);
        }
        break;

      case 'union':
        // Для простоты считаем элементы примитивами или сериализуемыми
        if (mode !== 'update' && !preserveExisting) {
          const set = new Set(targetArr);
          for (const item of sourceArr) {
            set.add(item);
          }
          targetArr.length = 0;
          targetArr.push(...set);
        }
        break;

      default:
        targetArr.length = 0;
        targetArr.push(...sourceArr);
    }
  }

  /**
   * Слияние Date.
   */
  function mergeDate(targetDate, sourceDate, parent, key) {
    const strategy = specialTypes.Date || 'replace';
    if (strategy === 'replace') {
      targetDate.setTime(sourceDate.getTime());
    } else if (strategy === 'skip') {
      // ничего не делаем
    } else {
      targetDate.setTime(sourceDate.getTime());
    }
  }

  /**
   * Слияние Map.
   */
  function mergeMap(targetMap, sourceMap, depth) {
    const strategy = specialTypes.Map || 'replace';
    if (strategy === 'replace') {
      targetMap.clear();
      for (const [k, v] of sourceMap) {
        targetMap.set(k, v);
      }
    } else if (strategy === 'merge' || strategy === 'update') {
      // 'merge' — обновляем существующие и добавляем новые
      // 'update' — только обновляем существующие ключи
      for (const [k, sourceVal] of sourceMap) {
        if (targetMap.has(k)) {
          const targetVal = targetMap.get(k);
          if (targetVal && typeof targetVal === 'object' && targetVal !== null &&
              sourceVal && typeof sourceVal === 'object' && sourceVal !== null) {
            // Если оба значения объекты одного типа, сливаем рекурсивно
            if (targetVal.constructor === sourceVal.constructor) {
              if (targetVal.constructor === Object) {
                // Рекурсивное слияние объектов
                mergeObject(targetVal, sourceVal, depth + 1);
              } else if (targetVal.constructor === Array) {
                mergeArray(targetVal, sourceVal, depth + 1);
              } else if (targetVal.constructor === Map) {
                mergeMap(targetVal, sourceVal, depth + 1);
              } else if (targetVal.constructor === Set) {
                mergeSet(targetVal, sourceVal);
              } else {
                // Для других типов используем стратегию замены
                targetMap.set(k, sourceVal);
              }
            } else {
              // Разные типы — используем conflictStrategy
              handleConflict(targetMap, k, targetVal, sourceVal);
            }
          } else {
            // Один из них не объект или один null — обновляем
            targetMap.set(k, sourceVal);
          }
        } else if (strategy === 'merge' && !preserveExisting && mode !== 'update') {
          // Добавляем новый ключ только если он не запрещен и это mode 'merge'
          targetMap.set(k, sourceVal);
        }
      }
      // Удаление отсутствующих ключей, если deleteMissing
      if (deleteMissing && strategy === 'merge') {
        for (const k of targetMap.keys()) {
          if (!sourceMap.has(k)) {
            targetMap.delete(k);
          }
        }
      }
    } // остальные стратегии можно добавить
  }

  /**
   * Слияние Set.
   */
  function mergeSet(targetSet, sourceSet) {
    const strategy = specialTypes.Set || 'replace';
    if (strategy === 'replace') {
      targetSet.clear();
      for (const v of sourceSet) {
        targetSet.add(v);
      }
    } else if (strategy === 'union') {
      // Добавляем все элементы источника в целевой Set
      for (const v of sourceSet) {
        targetSet.add(v);
      }
    } else if (strategy === 'merge') {
      // Для Set 'merge' действует как 'union' (добавляем элементы)
      for (const v of sourceSet) {
        targetSet.add(v);
      }
    }
    // 'update' не имеет смысла для Set, так как нет "существующих" ключей
    // 'delete' не входит в стандартные стратегии
  }

  // Запускаем слияние с каждым источником по очереди
  for (const source of sourcesToMerge) {
    if (source && typeof source === 'object') {
      // Определяем тип target и вызываем соответствующую функцию
      if (targetToMerge.constructor === Object || Object.getPrototypeOf(targetToMerge) === Object.prototype) {
        mergeObject(targetToMerge, source, 0);
      } else if (targetToMerge.constructor === Array) {
        if (source.constructor === Array) {
          mergeArray(targetToMerge, source, 0);
        } else {
          // Попытка слить non-array в array - используем стратегию конфликта
          handleConflict({ $: targetToMerge }, 0, targetToMerge, source);
        }
      } else if (targetToMerge.constructor === Map) {
        if (source.constructor === Map) {
          mergeMap(targetToMerge, source, 0);
        } else {
          handleConflict({ $: targetToMerge }, 0, targetToMerge, source);
        }
      } else if (targetToMerge.constructor === Set) {
        if (source.constructor === Set) {
          mergeSet(targetToMerge, source);
        } else {
          handleConflict({ $: targetToMerge }, 0, targetToMerge, source);
        }
      } else if (targetToMerge.constructor === Date) {
        if (source.constructor === Date) {
          mergeDate(targetToMerge, source, { $: targetToMerge }, 0);
        } else {
          handleConflict({ $: targetToMerge }, 0, targetToMerge, source);
        }
      } else {
        // Для других типов используем mergeKey стандартно, но с правильным родителем
        const wrapper = { target: targetToMerge };
        mergeKey(wrapper, 'target', targetToMerge, source, 0);
        // Обновляем target, если он был заменен
        // (для случаев, когда conflictStrategy === 'replace')
      }
    }
  }
  // Возвращаем итоговый target
  return targetToMerge;

  return target;
}


/**
 * Универсальная функция глубокого слияния (assign) с расширенными возможностями.
 *
 * @overload
 * @param {object} target - Целевой объект (будет мутирован).
 * @param {...object} sources - Объекты-источники.
 * @returns {object} Целевой объект после слияния (тот же, что и target).
 * @description При вызове с несколькими аргументами выполняется глубокое рекурсивное слияние
 *              объектов с использованием стандартных опций:
 *              - mode: 'merge'
 *              - arrayStrategy: 'replace'
 *              - conflictStrategy: 'replace'
 *              - deep: true
 *              - circular: 'ignore'
 *              - preserveExisting: false
 *              - deleteMissing: false
 *
 * @overload
 * @param {object} config - Объект конфигурации.
 * @param {object[]} config.objects - Массив объектов для слияния. Первый элемент массива
 *                                     будет целевым объектом (мутируется), остальные – источниками.
 * @param {Options} [config.options] - Объект с опциями, управляющими слиянием.
 * @returns {object} Целевой объект (первый в массиве objects) после применения слияния.
 *
 * @typedef {Object} Options
 * @property {'merge'|'assign'|'update'|'prune'|'mergeUpdate'} [mode='merge'] - Режим работы:
 *   - 'merge': рекурсивное слияние объектов и массивов согласно другим опциям.
 *   - 'assign': поверхностное копирование (аналог Object.assign).
 *   - 'update': только обновлять существующие ключи в цели, новые не добавлять.
 *   - 'prune': обновлять существующие и удалять из цели ключи, которых нет в источнике.
 *   - 'mergeUpdate': глубокое обновление, где числовые ключи в источнике выступают как индексы массива в цели.
 *     Позволяет обновлять конкретные элементы массивов через объект с числовыми ключами.
 *
 * @property {'replace'|'merge'|'concat'|'union'} [arrayStrategy='replace'] - Стратегия обработки массивов:
 *   - 'replace': массив источника полностью заменяет массив цели.
 *   - 'merge': рекурсивно сливать элементы по индексам (как объекты).
 *   - 'concat': конкатенировать массивы (цель + источник).
 *   - 'union': объединять уникальные значения (для массивов примитивов).
 *
 * @property {'replace'|'skip'|'throw'} [conflictStrategy='replace'] - Действие при конфликте типов
 *   (например, в цели число, а в источнике объект):
 *   - 'replace': заменить значением источника.
 *   - 'skip': оставить значение цели без изменений.
 *   - 'throw': выбросить ошибку.
 *
 * @property {boolean} [preserveExisting=false] - Если true, не перезаписывать существующие ключи
 *   (только добавлять новые). Противоположно режиму 'update', но может комбинироваться с другими опциями.
 *
 * @property {boolean} [deleteMissing=false] - Если true, удалять из цели ключи, отсутствующие в источнике.
 *   При deep:true применяется рекурсивно.
 *
 * @property {boolean} [deep=true] - Если true, изменения применяются рекурсивно на всех уровнях вложенности.
 *   Если false, работа ограничена верхним уровнем.
 *
 * @property {number} [maxDepth] - Максимальная глубина рекурсии. При превышении выбрасывается ошибка
 *   (если circular: 'error') или обход прекращается.
 *
 * @property {'ignore'|'replace'|'error'} [circular='ignore'] - Поведение при обнаружении циклических ссылок:
 *   - 'ignore': пропускать циклический участок (с риском бесконечной рекурсии, поэтому внутри используется WeakSet).
 *   - 'replace': заменять циклическую ссылку на undefined или null (можно настроить).
 *   - 'error': выбрасывать ошибку.
 *
 * @property {Object.<string, 'replace'|'merge'|'union'|'update'>} [specialTypes] - Стратегии для
 *   специальных типов объектов. Ключ — имя конструктора (например, 'Date', 'Map', 'Set').
 *   Значение определяет, как обрабатывать объекты этого типа при слиянии:
 *   - 'replace': полностью заменить объект источника (по умолчанию).
 *   - 'merge': обновлять только существующие элементы/ключи (для Map, Set).
 *   - 'union': объединять множества (для Set).
 *   - 'update': обновлять существующие ключи, не добавляя новые (для Map).
 *
 * @example
 * // Обычный вызов с несколькими аргументами
 * const target = { a: 1, b: { c: 2 } };
 * const source = { b: { d: 3 }, e: 4 };
 * assign(target, source);
 * // target: { a: 1, b: { c: 2, d: 3 }, e: 4 }
 *
 * @example
 * // Вызов с конфигурацией и опциями
 * const obj1 = { a: 1, items: [1, 2] };
 * const obj2 = { a: 2, items: [2, 3, 4] };
 * assign({
 *   objects: [obj1, obj2],
 *   options: {
 *     mode: 'merge',
 *     arrayStrategy: 'concat',
 *     conflictStrategy: 'skip'
 *   }
 * });
 * // obj1: { a: 1, items: [1, 2, 2, 3, 4] }
 *
 * @example
 * // Режим mergeUpdate: обновление элементов массива через числовые ключи
 * const target = {
 *   stages: [{
 *     actions: [
 *       { name: 'add', description: '' },
 *       { name: 'remove', estimates_id: '', description: '' }
 *     ]
 *   }]
 * };
 * const source = {
 *   stages: [{
 *     actions: {
 *       '1': { estimates_id: -1 }
 *     }
 *   }]
 * };
 * assign({
 *   objects: [target, source],
 *   options: { mode: 'mergeUpdate' }
 * });
 * // target.stages[0].actions[1].estimates_id === -1
 */
export function assign(targetOrConfig, ...sources) {
  // Если первый аргумент – объект с полем objects, считаем это конфигурацией
  if (typeof targetOrConfig === 'object' && targetOrConfig !== null && 'objects' in targetOrConfig) {
    const { objects, options = {} } = targetOrConfig;
    // objects должен быть массивом объектов для слияния
    if (!Array.isArray(objects)) throw new Error('objects must be an array');
    // Если objects пуст, возвращаем null или что-то? Лучше вернуть пустой объект
    if (objects.length === 0) return {};
    
    // Для mergeUpdate используем arrayStrategy 'merge' по умолчанию
    const mergedOptions = options.mode === 'mergeUpdate' && options.arrayStrategy === undefined
      ? { ...options, arrayStrategy: 'merge' }
      : options;
    
    // Используем первый объект как цель, остальные как источники
    return deepAssignWithOptions(objects[0], objects.slice(1), mergedOptions);
  } else {
    // Обычный режим: первый аргумент – цель, остальные – источники
    // По умолчанию используем разумные опции (рекурсивное слияние, замена массивов)
    const defaultOptions = {
      mode: 'merge',
      arrayStrategy: 'replace',
      conflictStrategy: 'replace',
      circular: 'ignore'
    };
    return deepAssignWithOptions(targetOrConfig, sources, defaultOptions);
  }
}